/**
 * 登録済みのカードの画像を管理する（Issue #928）: カードを探して、画像ごとに既定にする・型番を付ける・消す。
 * 既定の画像は、デッキにカードを足すとき（その言語）とカードを探すときに使われる（サーバーの `card_images.py`）。
 * 型番の候補はそのカードの収録パック（`GetCardSetCodes`）。同じ言語で型番が重なる画像には「重複」を出す（スキャンの重なりを消す目安）。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import CardRegionZoom, { CARD_REGIONS } from './CardRegionZoom';
import ZoomableImage from './ZoomableImage';
import RarityField, { raritiesFor } from './RarityField';

const LANG_LABEL = { jp: '日本語', en: 'English' };
const LANGS = ['jp', 'en'];

/**
 * 画像 1 枚の行（既定・型番・削除）。heading は上に出す見出し、allowLanguage で言語の付け替えも（Issue #930）
 */
export function ImageRow({ img, duplicate, listId, onChanged, heading, allowLanguage = false, rarityOptions }) {
  const [code, setCode] = useState(img.set_code ?? '');
  const [rarity, setRarity] = useState(img.rarity ?? '');
  useEffect(() => { setRarity(img.rarity ?? ''); }, [img.rarity]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCode(img.set_code ?? ''); }, [img.set_code]);

  const run = async (action, param) => {
    setBusy(true);
    try {
      await api.project(action, param);
      await onChanged();
    } catch (err) {
      alert('失敗: ' + err.message);
    } finally {
      setBusy(false);
    }
  };
  const saveCode = () => {
    if ((img.set_code ?? '') === code.trim().toUpperCase()) return;
    run('SetCardImageSetCode', { card_image_id: img.id, set_code: code });
  };
  const saveRarity = () => {
    if ((img.rarity ?? '') === rarity.trim().toUpperCase()) return;
    run('SetCardImageRarity', { card_image_id: img.id, rarity });
  };
  const remove = () => {
    if (!window.confirm(`${img.language_code} #${img.image_index} の画像を消しますか？（使っているデッキは別の画像に付け替えます）`)) return;
    run('DeleteCardImage', { card_image_id: img.id });
  };

  return (
    <div className="card" data-testid={`card-image-${img.id}`}
      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', border: img.is_default ? '1px solid #e94560' : undefined }}>
      <ZoomableImage src={img.download_url} alt={`${img.language_code}-${img.image_index}`} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {heading && <div className="card-meta">{heading}</div>}
        <div className="card-meta">
          {allowLanguage && `${LANG_LABEL[img.language_code] ?? img.language_code} `}#{String(img.image_index).padStart(2, '0')}
          {img.is_default && <strong style={{ marginLeft: 8, color: '#e94560' }}>★ 既定</strong>}
          {duplicate && <span style={{ marginLeft: 8, color: '#f0a020' }}>型番が重複</span>}
          {img.deck_count != null && <span style={{ marginLeft: 8 }}>デッキで {img.deck_count} 枚使用</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <CardRegionZoom src={img.download_url} region={CARD_REGIONS.setCode} width={280} title="型番拡大（絵の右下）" label="型番"
            testId={`set-code-zoom-${img.id}`} />
          <input className="form-input" style={{ width: 160 }} placeholder="型番（例 SD01-JP001）" list={listId}
            data-testid={`set-code-${img.id}`}
            value={code} disabled={busy}
            onChange={(e) => setCode(e.target.value)}
            onBlur={saveCode}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
          <RarityField id={`rarity-${img.id}`} testId={`rarity-${img.id}`} value={rarity} disabled={busy}
            options={rarityOptions} onChange={setRarity} onBlur={saveRarity} />
        </div>
        <div className="actions">
          {!img.is_default && (
            <button className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => run('SetCardImageDefault', { card_image_id: img.id })}>既定にする</button>
          )}
          {allowLanguage && LANGS.filter((l) => l !== img.language_code).map((l) => (
            <button key={l} className="btn btn-secondary btn-sm" disabled={busy}
              onClick={() => run('SetCardImageLanguage', { card_image_id: img.id, language_code: l })}>
              {LANG_LABEL[l]}に付け替える
            </button>
          ))}
          <button className="btn btn-danger btn-sm" disabled={busy} onClick={remove}>削除</button>
        </div>
      </div>
    </div>
  );
}

export default function CardImageManager() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [card, setCard] = useState(null);       // { card_id, card_name }
  const [images, setImages] = useState([]);
  const [setCodes, setSetCodes] = useState([]);

  useEffect(() => {
    const q = query.trim();
    if (!q || /^\d+$/.test(q)) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      try {
        setResults((await api.project('SearchCards', { query: q, limit: 20 })) ?? []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(async (cardId) => {
    const [imgs, codes] = await Promise.all([
      api.project('GetCardImage', { card_id: String(cardId) }),
      api.project('GetCardSetCodes', { card_id: String(cardId) }).catch(() => []),
    ]);
    setImages(imgs ?? []);
    setSetCodes(codes ?? []);
  }, []);

  const pick = async (next) => {
    setCard(next);
    setResults([]);
    await load(next.card_id);
  };
  const pickById = async () => {
    const id = query.trim().replace(/^0+/, '');
    if (!/^\d+$/.test(id)) return;
    let name = null;
    try { name = (await api.card('GetCardInfo', { card_id: id })).name ?? null; } catch { /* 名前は無くてよい */ }
    await pick({ card_id: id, card_name: name });
  };

  const langs = [...new Set(images.map((i) => i.language_code))];
  const dupKey = (i) => `${i.language_code}/${i.set_code}/${i.rarity ?? ''}`;   // 重複は 型番 ＋ レアリティ（Issue #931）
  const counts = images.reduce((m, i) => (i.set_code ? { ...m, [dupKey(i)]: (m[dupKey(i)] ?? 0) + 1 } : m), {});
  const listId = card ? `set-codes-${card.card_id}` : undefined;

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-title" style={{ marginBottom: 12 }}>登録済みの画像（既定・型番・削除）</div>
      <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
        onSubmit={(e) => { e.preventDefault(); pickById(); }}>
        <input className="form-input" style={{ width: 260 }} placeholder="カード名 か カードID" data-testid="card-image-query"
          value={query} onChange={(e) => setQuery(e.target.value)} />
        <button type="submit" className="btn btn-secondary btn-sm" disabled={!/^\d+$/.test(query.trim())}>ID で開く</button>
      </form>
      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8, maxHeight: 240, overflowY: 'auto' }}>
          {results.map((r) => (
            <button key={r.card_id} type="button" className="btn btn-secondary btn-sm" style={{ textAlign: 'left' }}
              onClick={() => pick({ card_id: r.card_id, card_name: r.card_name })}>
              {r.card_name}（{r.card_id}）
            </button>
          ))}
        </div>
      )}

      {card && (
        <div style={{ marginTop: 16 }}>
          <div className="card-meta" style={{ marginBottom: 8 }}>
            <strong>{card.card_name ?? '不明'}</strong>（{card.card_id}）— {images.length} 枚
          </div>
          <datalist id={listId}>
            {setCodes.map((c) => <option key={c.set_code} value={c.set_code}>{c.pack_name ?? ''}</option>)}
          </datalist>
          {images.length === 0 && <p className="loading">画像がありません</p>}
          {langs.map((lang) => (
            <div key={lang} style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: '0.9rem', color: '#aaa', margin: '8px 0' }}>{LANG_LABEL[lang] ?? lang}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {images.filter((i) => i.language_code === lang).map((img) => (
                  <ImageRow key={img.id} img={img} listId={listId} rarityOptions={raritiesFor(setCodes, img.set_code)}
                    duplicate={!!img.set_code && counts[dupKey(img)] > 1}
                    onChanged={() => load(card.card_id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
